import React, { useState } from "react";

import { TFunction, useTranslation } from "react-i18next";

import { SwapOutlined, SwapRightOutlined } from "@ant-design/icons";
import { Table } from "antd";
import { SortOrder } from "antd/es/table/interface";
import { Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/lib/table/interface";

import { DeeplinkComponent } from "../../../../components/deeplink";
import { DeeplinkModal } from "../../../../components/modals";
import { ClickModal } from "../../../../components/modals/click_modal";
import { ColumnTitle } from "../../../../components/table";
import { ScrollTableNotification } from "../../../../components/table/scroll_notification";
import { BookingItem } from "../../../../store/bookings/types";
import { Deeplink } from "../../../../store/common_types";
import { formatNumber, getSortOrder } from "../../../../utils/table";

const columns = (
  t: TFunction<string[]>,
  ordering: string,
  onDeeplinkClick: (deeplink: Deeplink) => void,
  onMarkerClick: (marker: string) => void,
  filteredValues?: FilteredValues,
  currency?: string
) => {
  return [
    {
      key: "orderNumber",
      title: <ColumnTitle title={t("table.columns.orderNumber.title")} />,
      dataIndex: "orderNumber",
    },
    {
      key: "state",
      title: <ColumnTitle title={t("table.columns.state.title")} helpText={t("table.columns.state.helpText")} />,
      dataIndex: "state",
      filteredValue: filteredValues?.state || [],
      filters: [
        {
          text: t("table.columns.state.enums.paid"),
          value: "paid",
        },
        {
          text: t("table.columns.state.enums.cancelled"),
          value: "cancelled",
        },
        {
          text: t("table.columns.state.enums.processing"),
          value: "processing",
        },
      ],
      render: (text: string) => t(`table.columns.state.enums.${text}`),
    },
    {
      key: "host",
      title: <ColumnTitle title={t("table.columns.host.title")} helpText={t("table.columns.host.helpText")} />,
      dataIndex: "host",
    },
    {
      key: "deeplink",
      title: (
        <ColumnTitle
          title={t("table.columns.deeplink.title")}
          helpText={t("table.columns.deeplink.helpText", { currency })}
        />
      ),
      dataIndex: "deeplink",
      render: (deeplink?: Deeplink) =>
        deeplink && <DeeplinkComponent deeplink={deeplink} onDeeplinkClick={onDeeplinkClick} />,
    },
    {
      key: "price",
      title: (
        <ColumnTitle
          title={t("table.columns.price.title")}
          helpText={t("table.columns.price.helpText", { currency: `$t(currency_selector:codes.${currency}.plural)` })}
        />
      ),
      dataIndex: "price",
      align: "right" as const,
      render: (price: number) => formatNumber(price),
      sorter: true,
      sortOrder: getSortOrder("price", ordering),
      showSorterTooltip: false,
    },
    {
      key: "searchUserIp",
      align: "right" as const,
      title: (
        <ColumnTitle
          title={t("table.columns.searchUserIp.title")}
          helpText={t("table.columns.searchUserIp.helpText")}
        />
      ),
      dataIndex: "searchUserIp",
    },
    {
      key: "directions",
      align: "center" as const,
      title: (
        <ColumnTitle title={t("table.columns.directions.title")} helpText={t("table.columns.directions.helpText")} />
      ),
      render: (record: BookingItem) => (
        <>
          {record.origin}
          {record.isRoundTrip ? (
            <SwapOutlined style={{ margin: "0 5px", transform: "scale(1, -1)" }} />
          ) : (
            <SwapRightOutlined style={{ margin: "0 5px" }} />
          )}
          {record.destination}
        </>
      ),
    },
    {
      key: "marker",
      title: <ColumnTitle title={t("table.columns.marker.title")} />,
      dataIndex: "marker",
      render: (value?: string) => value && <a onClick={() => onMarkerClick(value)}>{value}</a>,
    },
    {
      key: "createdAt",
      title: <ColumnTitle title={t("table.columns.datetime.title")} helpText={t("table.columns.datetime.helpText")} />,
      dataIndex: "createdAt",
      sorter: true,
      sortOrder: getSortOrder("createdAt", ordering),
      showSorterTooltip: false,
    },
  ];
};

export interface FilteredValues {
  state: string[];
}

export interface DataTableProps {
  totalBookings: number;
  dataSource: BookingItem[];
  filtered?: FilteredValues;
  ordering: string;
  currentCurrency?: string;
  currentPage: number;
  pageSize?: number;
  loading?: boolean;
  onChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<BookingItem> | SorterResult<BookingItem>[],
    extra: TableCurrentDataSource<BookingItem>
  ) => void;
}

export const DataTable: React.FC<DataTableProps> = (props) => {
  const { t, i18n } = useTranslation(["bookings_page", "common"]);
  const [deeplinkModal, setDeeplinkModal] = useState<{ visible: boolean; deeplink: Deeplink | undefined }>({
    visible: false,
    deeplink: undefined,
  });
  const [clickDrawer, setClickDrawer] = useState<{ visible: boolean; marker: string | undefined }>({
    visible: false,
    marker: undefined,
  });

  const handleDeeplinkClick = (deeplink: Deeplink) => {
    setDeeplinkModal({ visible: true, deeplink });
  };

  const handleDeeplinkModalClose = () => {
    setDeeplinkModal((state) => ({ ...state, visible: false }));
  };

  const handleMarkerClick = (marker: string) => {
    setClickDrawer({ visible: true, marker });
  };

  const handleClickDrawerClose = () => {
    setClickDrawer((state) => ({ ...state, visible: false }));
  };

  return (
    <ScrollTableNotification>
      <DeeplinkModal onClose={handleDeeplinkModalClose} {...deeplinkModal} />
      <ClickModal onClose={handleClickDrawerClose} onDeeplinkClick={handleDeeplinkClick} {...clickDrawer} />
      <Table
        bordered
        loading={props.loading}
        columns={columns(
          t,
          props.ordering,
          handleDeeplinkClick,
          handleMarkerClick,
          props.filtered,
          props.currentCurrency?.toUpperCase()
        )}
        sortDirections={["ascend", "descend", "ascend"] as SortOrder[]}
        dataSource={props.dataSource}
        onChange={props.onChange}
        rowKey={(record: BookingItem) => `bookings-row-${record.orderNumber}`}
        footer={() => `${i18n.t("common:table.total")}: ${props.totalBookings}`}
        pagination={{
          total: props.totalBookings,
          current: props.currentPage,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
          pageSize: props.pageSize,
        }}
        size="small"
      />
    </ScrollTableNotification>
  );
};
